import { realtimeTrigger } from '@/state/realtime';
import { IdChecklistLogger } from '@/utils/logger';
import { addSentryBreadcrumb, captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import {
  canTransferChecklist,
  getTransferableChecklistRecipients,
  type ChecklistAssignmentCandidate,
} from '@/utils/checklistAssignment';
export const CHECKLIST_TRANSFER_RPC = 'transfer_checklist_owner_v1';
export type ChecklistTransferErrorCode =
  | 'offline' | 'feature_unavailable' | 'not_authorized' | 'pending_changes'
  | 'rpc_conflict' | 'rpc_failure' | 'invalid_response' | 'reconcile_failure';
export type ChecklistTransferChecklist = {
  id: string;
  empresa_id?: string | null; usuario_id?: string | null; status?: string | null;
  data_conclusao?: unknown; updated_at?: unknown;
  [key: string]: unknown;
};
export type ChecklistTransferActor = {
  id?: string | null; empresa_id?: string | null; user_type?: string | null;
};
export type ChecklistTransferOperationScope = {
  actorId: string; companyId: string; checklistId: string;
  previousOwnerId: string; targetUserId: string; expectedUpdatedAt: string;
};
export type ChecklistTransferRpcArgs = {
  p_operation_id: string; p_checklist_id: string; p_target_user_id: string;
  p_expected_owner_id: string; p_expected_updated_at: string; p_client_build: number;
};
export type ChecklistTransferCandidatesRpcArgs = {
  p_checklist_id: string; p_client_build: number;
};
export interface ChecklistTransferOperationStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}
export type ChecklistTransferStoredOperation = {
  operationId: string; rpcScope: ChecklistTransferOperationScope;
};
const OPERATION_PREFIX = '@appguincho/checklist-transfer:v1:';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PENDING_TABLES = ['pending_operations', 'pending_images', 'pending_deletions', 'sync_conflicts'] as const;
const PENDING_KEYS = ['item_id', 'itemId', 'checklist_id', 'checklistId', 'entity_id', 'entityId'] as const;
const FEATURE_KEY = 'checklist_transfer_v1';
const normalized = (value: unknown): string => typeof value === 'string' ? value.trim() : '';
const shortId = (value: unknown): string => String(value ?? '').trim().slice(-6) || 'n/a';
const operationKey = (scope: ChecklistTransferOperationScope): string => (
  OPERATION_PREFIX + [scope.actorId, scope.companyId, scope.checklistId, scope.targetUserId]
    .map(encodeURIComponent).join(':')
);
const isValidScope = (value: unknown): value is ChecklistTransferOperationScope => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return ['actorId', 'companyId', 'checklistId', 'previousOwnerId', 'targetUserId', 'expectedUpdatedAt']
    .every(key => Boolean(normalized(record[key])));
};
export const createChecklistTransferOperationStore = (
  storage: ChecklistTransferOperationStorage, createOperationId: () => string,
) => {
  const findOperation = async (scope: ChecklistTransferOperationScope): Promise<ChecklistTransferStoredOperation | null> => {
    const raw = await storage.getItem(operationKey(scope));
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { operationId?: unknown; rpcScope?: unknown };
      const operationId = normalized(parsed.operationId);
      if (!UUID_PATTERN.test(operationId) || !isValidScope(parsed.rpcScope)) return null;
      const rpcScope = parsed.rpcScope;
      if (
        rpcScope.actorId !== scope.actorId || rpcScope.companyId !== scope.companyId
        || rpcScope.checklistId !== scope.checklistId || rpcScope.targetUserId !== scope.targetUserId
      ) return null;
      return { operationId, rpcScope };
    } catch {
      return null;
    }
  };
  const getOrCreateOperationId = async (scope: ChecklistTransferOperationScope): Promise<string> => {
    const stored = await findOperation(scope);
    if (stored) return stored.operationId;
    const operationId = createOperationId();
    if (!UUID_PATTERN.test(operationId)) throw new Error('operation_id_invalid');
    await storage.setItem(operationKey(scope), JSON.stringify({ operationId, rpcScope: scope }));
    return operationId;
  };
  const clearOperationId = (scope: ChecklistTransferOperationScope): Promise<void> => (
    storage.removeItem(operationKey(scope))
  );
  return { findOperation, getOrCreateOperationId, clearOperationId };
};
export interface ChecklistTransferDatabase {
  listItems<T>(collection: string, query?: Record<string, unknown>): Promise<T[]>;
  readItem<T>(collection: string, id: string): Promise<T | null>;
  updateItem<T>(collection: string, id: string, updates: Partial<T>,
    options?: { skipPendingOperation?: boolean; source?: 'local' | 'remote' }): Promise<boolean>;
}
export interface ChecklistTransferDependencies {
  database: ChecklistTransferDatabase;
  isOnline: () => boolean;
  getClientBuild: () => number;
  isFeatureEnabled(input: { companyId: string; featureKey: string; clientBuild: number }): Promise<boolean>;
  pushLocalChanges: () => Promise<{ success: boolean }>;
  refreshChecklist: (checklistId: string) => Promise<{ success: boolean }>;
  findOperation(scope: ChecklistTransferOperationScope): Promise<ChecklistTransferStoredOperation | null>;
  getOrCreateOperationId(scope: ChecklistTransferOperationScope): Promise<string>;
  clearOperationId(scope: ChecklistTransferOperationScope): Promise<void>;
  listCandidates(args: ChecklistTransferCandidatesRpcArgs): Promise<unknown>;
  callRpc(args: ChecklistTransferRpcArgs): Promise<unknown>;
  checklistTable: string;
}
export type ChecklistTransferInput = {
  checklist: ChecklistTransferChecklist; actor: ChecklistTransferActor; targetUserId: string;
};
export type ChecklistTransferResult = {
  checklist: ChecklistTransferChecklist; outcome: string; revokedLinksCount: number;
};
const parseObject = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
const responseObject = (value: unknown): Record<string, unknown> | null => (
  parseObject(value) || (Array.isArray(value) ? parseObject(value[0]) : null)
);
const sameValue = (left: unknown, right: unknown): boolean => {
  if (left == null && right == null) return true;
  const leftTime = Date.parse(String(left));
  const rightTime = Date.parse(String(right));
  return Number.isFinite(leftTime) && Number.isFinite(rightTime)
    ? leftTime === rightTime : String(left ?? '') === String(right ?? '');
};
const rpcTimestamp = (value: unknown): string => {
  const date = new Date(typeof value === 'number' && value < 10_000_000_000 ? value * 1000 : value as any);
  if (!Number.isFinite(date.getTime())) {
    throw new ChecklistTransferError('Não foi possível confirmar a versão do checklist. Sincronize e tente novamente.', 'rpc_failure');
  }
  return date.toISOString();
};
const errorCode = (error: unknown): string => {
  if (!error || typeof error !== 'object') return '';
  const code = (error as Record<string, unknown>).code;
  return typeof code === 'string' ? code.slice(0, 32) : '';
};
const referencesChecklist = (row: unknown, checklistId: string): boolean => {
  if (!row || typeof row !== 'object') return false;
  const record = row as Record<string, unknown>;
  if (PENDING_KEYS.some(key => String(record[key] ?? '') === checklistId)) return true;
  const payload = parseObject(record.data);
  return Boolean(payload && PENDING_KEYS.some(key => String(payload[key] ?? '') === checklistId));
};
const captureUnexpected = (stage: string, checklistId: string, code = ''): void => {
  captureSentryExceptionWithScope(new Error(`checklist_transfer_${stage}`), {
    context: { flow: 'checklist_transfer', stage, checklist: shortId(checklistId), code: code || 'n/a' },
    tags: { flow: 'checklist_transfer', stage },
    fingerprint: ['checklist_transfer', stage],
    dedupeKey: `checklist_transfer:${stage}:${shortId(checklistId)}:${code || 'n/a'}`,
  });
};
export class ChecklistTransferError extends Error {
  constructor(message: string, readonly code: ChecklistTransferErrorCode) {
    super(message);
    this.name = 'ChecklistTransferError';
  }
}
/** Coordena sync, RPC e reconciliação local sem registrar uma nova outbox. */
export class ChecklistTransferService {
  private readonly inFlight = new Map<string, Promise<ChecklistTransferResult>>();
  constructor(private readonly dependencies: ChecklistTransferDependencies) {}
  async isFeatureEnabled(companyId: string): Promise<boolean> {
    if (!normalized(companyId) || !this.dependencies.isOnline()) return false;
    return this.dependencies.isFeatureEnabled({
      companyId,
      featureKey: FEATURE_KEY,
      clientBuild: this.dependencies.getClientBuild(),
    }).catch(() => false);
  }
  async getCandidates(
    checklist: ChecklistTransferChecklist, actor: ChecklistTransferActor,
  ): Promise<ChecklistAssignmentCandidate[]> {
    if (!this.dependencies.isOnline()) {
      throw new ChecklistTransferError(
        'Conecte-se à internet para transferir este checklist.',
        'offline',
      );
    }
    const companyId = normalized(checklist.empresa_id);
    if (!canTransferChecklist({
      actorId: actor.id,
      actorCompanyId: actor.empresa_id,
      actorUserType: actor.user_type,
      ownerId: checklist.usuario_id,
      checklistCompanyId: companyId,
    })) return [];
    try {
      const raw = await this.dependencies.listCandidates({
        p_checklist_id: checklist.id,
        p_client_build: this.dependencies.getClientBuild(),
      });
      if (!Array.isArray(raw)) throw new Error('candidate_response_invalid');
      return getTransferableChecklistRecipients(
        raw as ChecklistAssignmentCandidate[],
        companyId,
        checklist.usuario_id,
      );
    } catch (error) {
      const code = errorCode(error);
      if (!['42501', '22023'].includes(code)) captureUnexpected('candidate_list', checklist.id, code);
      throw new ChecklistTransferError(
        'Não foi possível carregar os responsáveis elegíveis.',
        code === '42501' ? 'not_authorized' : 'rpc_failure',
      );
    }
  }
  transfer(input: ChecklistTransferInput): Promise<ChecklistTransferResult> {
    const key = [input.actor.id, input.actor.empresa_id, input.checklist.id, input.targetUserId].join(':');
    const active = this.inFlight.get(key);
    if (active) return active;
    const request = this.transferExclusive(input);
    this.inFlight.set(key, request);
    const clear = () => { if (this.inFlight.get(key) === request) this.inFlight.delete(key); };
    void request.then(clear, clear);
    return request;
  }
  private async transferExclusive(input: ChecklistTransferInput): Promise<ChecklistTransferResult> {
    const checklistId = normalized(input.checklist.id);
    const companyId = normalized(input.checklist.empresa_id);
    const ownerId = normalized(input.checklist.usuario_id);
    const targetUserId = normalized(input.targetUserId);
    const actorId = normalized(input.actor.id);
    if (!checklistId || !companyId || !ownerId || !targetUserId || !actorId) {
      throw new ChecklistTransferError('Dados do checklist ou usuário inválidos.', 'not_authorized');
    }
    if (!this.dependencies.isOnline()) {
      throw new ChecklistTransferError(
        'Conecte-se à internet para transferir este checklist.',
        'offline',
      );
    }
    const initialScope: ChecklistTransferOperationScope = {
      actorId,
      companyId,
      checklistId,
      previousOwnerId: ownerId,
      targetUserId,
      expectedUpdatedAt: rpcTimestamp(input.checklist.updated_at),
    };
    const enabled = await this.dependencies.isFeatureEnabled({
      companyId,
      featureKey: FEATURE_KEY,
      clientBuild: this.dependencies.getClientBuild(),
    }).catch(() => {
      throw new ChecklistTransferError(
        'A transferência não está disponível no momento. Tente novamente.',
        'feature_unavailable',
      );
    });
    if (!enabled) {
      throw new ChecklistTransferError(
        'A transferência ainda não está disponível para esta empresa.',
        'feature_unavailable',
      );
    }
    let stored: ChecklistTransferStoredOperation | null;
    try {
      stored = await this.dependencies.findOperation(initialScope);
    } catch {
      captureUnexpected('operation_key', checklistId);
      throw new ChecklistTransferError('Não foi possível preparar a transferência. Tente novamente.', 'rpc_failure');
    }
    const canonicalBefore = await this.prepareCanonical(checklistId, !stored);
    let rpcScope = stored?.rpcScope;
    if (!rpcScope) {
      const canonicalOwner = normalized(canonicalBefore.usuario_id);
      if (!canTransferChecklist({
        actorId,
        actorCompanyId: input.actor.empresa_id,
        actorUserType: input.actor.user_type,
        ownerId: canonicalOwner,
        checklistCompanyId: canonicalBefore.empresa_id,
      })) {
        throw new ChecklistTransferError(
          'O checklist mudou de responsável. Atualize a tela e tente novamente.',
          'rpc_conflict',
        );
      }
      rpcScope = {
        actorId,
        companyId,
        checklistId,
        previousOwnerId: canonicalOwner,
        targetUserId,
        expectedUpdatedAt: rpcTimestamp(canonicalBefore.updated_at),
      };
    }
    let operationId: string;
    try {
      operationId = stored?.operationId ?? await this.dependencies.getOrCreateOperationId(rpcScope);
    } catch {
      captureUnexpected('operation_key', checklistId);
      throw new ChecklistTransferError('Não foi possível preparar a transferência. Tente novamente.', 'rpc_failure');
    }
    addSentryBreadcrumb({
      category: 'checklist.transfer',
      message: 'Solicitação de transferência preparada.',
      level: 'info',
      data: { checklist: shortId(checklistId), actor: shortId(actorId), target: shortId(targetUserId) },
    });
    let payload: Record<string, unknown>;
    try {
      const raw = await this.dependencies.callRpc({
        p_operation_id: operationId,
        p_checklist_id: checklistId,
        p_target_user_id: targetUserId,
        p_expected_owner_id: rpcScope.previousOwnerId,
        p_expected_updated_at: rpcScope.expectedUpdatedAt,
        p_client_build: this.dependencies.getClientBuild(),
      });
      payload = responseObject(raw) ?? {};
    } catch (error) {
      const code = errorCode(error);
      const conflict = ['40001', '40P01', '55P03'].includes(code);
      if (!conflict && !['42501', '22023'].includes(code)) captureUnexpected('rpc', checklistId, code);
      throw new ChecklistTransferError(
        conflict
          ? 'O checklist foi alterado antes da transferência. Atualize a tela e tente novamente.'
          : 'Não foi possível transferir o checklist agora. Tente novamente.',
        conflict ? 'rpc_conflict' : 'rpc_failure',
      );
    }
    const canonical = parseObject(payload.checklist) as ChecklistTransferChecklist | null;
    const retry = normalized(payload.outcome) === 'idempotent';
    if (
      !canonical || canonical.id !== checklistId
      || normalized(canonical.empresa_id) !== companyId
      || normalized(canonical.usuario_id) !== targetUserId
      || (!retry && canonical.status !== canonicalBefore.status)
      || (!retry && !sameValue(canonical.data_conclusao, canonicalBefore.data_conclusao))
    ) {
      captureUnexpected('invalid_response', checklistId);
      throw new ChecklistTransferError(
        'A resposta do servidor não pôde ser confirmada. Nada foi alterado localmente.',
        'invalid_response',
      );
    }
    const localUpdates: Partial<ChecklistTransferChecklist> = retry
      ? { usuario_id: canonical.usuario_id, updated_at: canonicalBefore.updated_at }
      : {
          usuario_id: canonical.usuario_id,
          status: canonical.status,
          data_conclusao: canonical.data_conclusao,
          updated_at: canonical.updated_at,
        };
    const updated = await this.dependencies.database.updateItem(
      this.dependencies.checklistTable,
      checklistId,
      localUpdates,
      { skipPendingOperation: true, source: 'remote' },
    ).catch(() => false);
    const readback = updated
      ? await this.dependencies.database.readItem<ChecklistTransferChecklist>(
          this.dependencies.checklistTable,
          checklistId,
        ).catch(() => null)
      : null;
    if (!readback || normalized(readback.usuario_id) !== targetUserId) {
      captureUnexpected('reconcile', checklistId);
      throw new ChecklistTransferError(
        'A transferência foi confirmada, mas a atualização local falhou. Sincronize novamente.',
        'reconcile_failure',
      );
    }
    await this.dependencies.clearOperationId(rpcScope).catch(() => {
      IdChecklistLogger.Main.warn(
        `[checklist-transfer] chave idempotente não removida checklist=${shortId(checklistId)}`,
      );
    });
    realtimeTrigger.counter.set(value => value + 1);
    const revokedLinksCount = Number.isSafeInteger(Number(payload.revoked_links_count))
      ? Math.max(0, Number(payload.revoked_links_count)) : 0;
    const outcome = normalized(payload.outcome) || 'transferred';
    IdChecklistLogger.Main.info(
      `[checklist-transfer] sucesso checklist=${shortId(checklistId)} links_revogados=${revokedLinksCount}`,
    );
    return { checklist: canonical, outcome, revokedLinksCount };
  }
  private async prepareCanonical(checklistId: string, drainPending: boolean): Promise<ChecklistTransferChecklist> {
    if (drainPending) {
      let pending = await this.countRelatedPending(checklistId);
      if (pending > 0) {
        const pushed = await this.dependencies.pushLocalChanges().catch(() => ({ success: false }));
        pending = await this.countRelatedPending(checklistId);
        if (pending > 0) {
          throw new ChecklistTransferError(
            'Sincronize as alterações pendentes e tente novamente.',
            'pending_changes',
          );
        }
        if (!pushed.success) IdChecklistLogger.Main.warn(`[checklist-transfer] sync global parcial checklist=${shortId(checklistId)}`);
      }
    }
    const refreshed = await this.dependencies.refreshChecklist(checklistId)
      .catch(() => ({ success: false }));
    if (!refreshed.success) {
      throw new ChecklistTransferError(
        'Não foi possível sincronizar o checklist. Verifique a conexão e tente novamente.',
        'pending_changes',
      );
    }
    const canonical = await this.dependencies.database.readItem<ChecklistTransferChecklist>(
      this.dependencies.checklistTable,
      checklistId,
    ).catch(() => null);
    if (!canonical) {
      throw new ChecklistTransferError(
        'Não foi possível confirmar a versão sincronizada do checklist. Tente novamente.',
        'pending_changes',
      );
    }
    return canonical;
  }
  private async countRelatedPending(checklistId: string): Promise<number> {
    try {
      let total = 0;
      for (const table of PENDING_TABLES) {
        const rows = await this.dependencies.database.listItems<Record<string, unknown>>(table);
        total += rows.filter(row => (
          referencesChecklist(row, checklistId)
          && (table !== 'sync_conflicts' || row.status !== 'resolved')
        )).length;
      }
      return total;
    } catch {
      throw new ChecklistTransferError(
        'Não foi possível verificar as alterações pendentes. Tente novamente.',
        'pending_changes',
      );
    }
  }
}
export { FEATURE_KEY as CHECKLIST_TRANSFER_FEATURE };
